import { onAuthStateChanged, signInWithCustomToken, User } from "firebase/auth";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { authService } from "../../firebase-config";
import { soulApi } from "../api/SoulApi";

async function getCustomToken({
  referencedUser,
}: {
  referencedUser: string;
}): Promise<string> {
  const result = await soulApi.getCustomTokenAsAdmin.request({
    referencedUser,
  });
  if (!result?.isSuccess) {
    throw new Error("Error on getCustomToken");
  }
  return result.data?.token!;
}

const AuthContext = createContext<{
  user: User | null;
  loading: boolean;
  isOther: boolean;
  refreshUser: () => Promise<void>;
  loginAsOther: (customToken: string) => Promise<void>;
  logout: () => Promise<void>;
}>(null!);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isOther, setIsOther] = useState(false);

  useEffect(() => {
    const activeSession = localStorage.getItem("activeSession");
    if (activeSession === "other") {
      setIsOther(true);
    }
    const unsubscribe = onAuthStateChanged(authService, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const refreshUser = useCallback(async () => {
    if (authService.currentUser) {
      await authService.currentUser.reload();
      setUser(authService.currentUser);
    } else {
      setUser(null);
    }
  }, []);

  const loginAsOther = useCallback(async (customToken: string) => {
    if (authService.currentUser) {
      const adminUid = authService.currentUser.uid;
      const adminToken = await getCustomToken({ referencedUser: adminUid });
      localStorage.setItem("primaryUid", adminUid);
      localStorage.setItem("primaryToken", adminToken);
    }

    const cred = await signInWithCustomToken(authService, customToken);
    setUser(cred.user);
    setIsOther(true);

    localStorage.setItem("customToken", customToken);
    localStorage.setItem("activeUid", cred.user.uid);
    localStorage.setItem("activeSession", "other");
  }, []);

  const logout = useCallback(async () => {
    const adminToken = localStorage.getItem("primaryToken");

    if (adminToken) {
      const cred = await signInWithCustomToken(authService, adminToken);
      setUser(cred.user);
      setIsOther(false);

      localStorage.removeItem("customToken");
      localStorage.removeItem("activeUid");
      localStorage.removeItem("activeSession");
    } else {
      await authService.signOut();
      setUser(null);
      setIsOther(false);
      localStorage.clear();
    }

    window.location.href = "/dashboard/all-profile";
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isOther,
        refreshUser,
        loginAsOther,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
